import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/typeform.css";
import { useTheme } from "../theme/ThemeProvider";
export default function TypeformStyle({ config, onSubmit, onFileUpload, }) {
    const questions = useMemo(() => config.sections.flatMap((s) => s.fields), [config.sections]);
    const [i, setI] = useState(0);
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const inputRef = useRef(null);
    const totalScreens = questions.length + 1;
    const isSubmitScreen = i === questions.length;
    const progress = ((i + 1) / totalScreens) * 100;
    // Focus the active input when the component mounts and after navigation
    const focusInput = useCallback(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 150);
        return () => clearTimeout(t);
    }, []);
    useEffect(() => {
        const cleanup = focusInput();
        return cleanup;
    }, [focusInput]);
    const q = !isSubmitScreen ? (questions[i] ?? null) : null;
    const setValue = (id, val) => {
        setAnswers((prev) => ({ ...prev, [id]: val }));
        if (errors[id])
            setErrors((e) => ({ ...e, [id]: "" }));
    };
    const validate = (field) => {
        if (!field)
            return true;
        const val = answers[field.id];
        const empty = val === undefined ||
            val === null ||
            (typeof val === "string" && val.trim() === "") ||
            (Array.isArray(val) && val.length === 0);
        if (field.required && empty) {
            setErrors({ [field.id]: "This field is required" });
            return false;
        }
        return true;
    };
    const next = () => {
        if (!isSubmitScreen && !validate(q))
            return;
        setI((x) => Math.min(x + 1, totalScreens - 1));
        requestAnimationFrame(() => {
            focusInput();
        });
    };
    const prev = () => {
        setI((x) => Math.max(x - 1, 0));
        requestAnimationFrame(() => {
            focusInput();
        });
    };
    const handleSubmit = async () => {
        for (const f of questions) {
            const ok = validate(f);
            if (!ok) {
                const idx = questions.findIndex((ff) => ff.id === f.id);
                if (idx >= 0)
                    setI(idx);
                return;
            }
        }
        setSubmitting(true);
        try {
            const result = await onSubmit(answers);
            // Handle the result if it's a SubmitResult object
            if (result && typeof result === "object" && "success" in result) {
                if (result.success) {
                    setDone(config.successMessage || "Thanks — your response was submitted.");
                }
                else {
                    // Handle submission errors
                    if (result.validationErrors && result.validationErrors.length > 0) {
                        // Set field-specific validation errors
                        const fieldErrors = {};
                        for (const err of result.validationErrors) {
                            fieldErrors[err.field] = err.message;
                        }
                        setErrors({
                            ...fieldErrors,
                            general: result.error || "Please correct the following errors:",
                        });
                        // Navigate to the first field with an error
                        const firstErrorField = result.validationErrors[0]?.field;
                        if (firstErrorField) {
                            const idx = questions.findIndex((ff) => ff.id === firstErrorField);
                            if (idx >= 0)
                                setI(idx);
                        }
                    }
                    else {
                        // Set general error
                        setErrors({
                            general: result.error || "Submission failed. Please try again.",
                        });
                    }
                }
            }
            else {
                // Legacy behavior: assume success if no result or void
                setDone(config.successMessage || "Thanks — your response was submitted.");
            }
        }
        catch (error) {
            console.error("Submit error:", error);
            setErrors({ general: "An unexpected error occurred. Please try again." });
        }
        finally {
            setSubmitting(false);
        }
    };
    const { accent } = useTheme();
    useEffect(() => {
        if (typeof document !== "undefined") {
            const root = document.documentElement;
            root.style.setProperty("--tf-accent", accent === "orange"
                ? "#f97316"
                : accent === "green"
                    ? "#22c55e"
                    : accent === "violet"
                        ? "#7c3aed"
                        : "#6366f1");
        }
    }, [accent]);
    // Render input by type
    const renderInput = (field) => {
        if (!field)
            return null;
        const common = {
            id: field.id,
            name: field.id,
            placeholder: field.placeholder || "Type your answer…",
            "aria-label": field.label || undefined,
            onKeyDown: (e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                    if (field?.type !== "textarea") {
                        e.preventDefault();
                        next();
                    }
                }
            },
        };
        switch (field.type) {
            case "textarea":
                return (_jsx("textarea", { ref: inputRef, ...common, rows: 4, value: answers[field.id] ?? "", onChange: (e) => setValue(field.id, e.target.value), className: "tf-input" }));
            case "text":
            case "email":
            case "phone":
            case "number":
            case "date":
                return (_jsx("input", { ref: inputRef, type: field.type === "phone" ? "tel" : field.type, ...common, value: answers[field.id] ?? "", onChange: (e) => setValue(field.id, e.target.value), className: "tf-input" }));
            case "select":
                return (_jsxs("select", { ...common, ref: inputRef, value: answers[field.id] ?? "", onChange: (e) => setValue(field.id, e.target.value), className: "tf-input tf-select", children: [_jsx("option", { value: "", disabled: true, hidden: true, children: "Select\u2026" }), (field.options || []).map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value)))] }));
            case "radio":
                return (_jsx("div", { className: "tf-radios", children: (field.options || []).map((opt) => (_jsxs("label", { className: `tf-radio ${answers[field.id] === opt.value ? "active" : ""}`, children: [_jsx("input", { type: "radio", name: field.id, checked: answers[field.id] === opt.value, onChange: () => setValue(field.id, opt.value) }), opt.label] }, opt.value))) }));
            case "checkbox":
                return (_jsxs("label", { className: "tf-checkbox", children: [_jsx("input", { type: "checkbox", checked: Boolean(answers[field.id]), onChange: (e) => setValue(field.id, e.target.checked) }), field.label] }));
            case "file": {
                const items = answers[field.id] || [];
                const onPick = async (files) => {
                    if (!files || files.length === 0)
                        return;
                    const arr = Array.from(files);
                    const placeholders = arr.map((f) => ({
                        name: f.name,
                        size: f.size,
                        type: f.type,
                        progress: 0,
                        status: "uploading",
                    }));
                    setValue(field.id, [...items, ...placeholders]);
                    for (let idx = 0; idx < arr.length; idx++) {
                        const f = arr[idx];
                        if (!f)
                            continue;
                        let uploaded = null;
                        try {
                            if (onFileUpload) {
                                uploaded = await onFileUpload({
                                    fieldId: field.id,
                                    file: f,
                                    contentType: f.type,
                                    size: f.size,
                                    form: config,
                                });
                            }
                            else {
                                uploaded = { success: true };
                            }
                        }
                        catch {
                            uploaded = { success: false, error: "Upload failed" };
                        }
                        setAnswers((prev) => {
                            const list = (prev[field.id] || []).slice();
                            const target = list[items.length + idx];
                            if (target) {
                                target.progress = uploaded.success ? 100 : target.progress;
                                target.status = uploaded.success ? "done" : "error";
                                if (uploaded.url)
                                    target.url = uploaded.url;
                                if (uploaded.fileId)
                                    target.fileId = uploaded.fileId;
                            }
                            return { ...prev, [field.id]: list };
                        });
                    }
                };
                return (_jsxs("div", { children: [_jsx("input", { type: "file", ...common, ref: null, onChange: (e) => onPick(e.target.files), className: "tf-file", multiple: Boolean(field.multiple), accept: field.accept }), items.length > 0 && (_jsx("ul", { className: "tf-review-list", style: { marginTop: 8 }, children: items.map((it) => {
                                const key = it.fileId ?? `${it.name}-${it.size}-${it.type}`;
                                return (_jsxs("li", { children: [it.name, " \u2014 ", it.progress, "% ", it.status] }, key));
                            }) }))] }));
            }
            default:
                return null;
        }
    };
    return (_jsxs("div", { className: "tf-container", children: [_jsx("div", { className: "tf-title", children: config.title }), _jsx("div", { className: "tf-progress", style: { width: `${Math.round(progress)}%` } }), !isSubmitScreen ? (_jsxs("div", { children: [_jsxs("label", { className: "tf-label", htmlFor: q?.id, children: [q?.label, " ", q?.required ? _jsx("span", { "aria-hidden": "true", children: "*" }) : null] }), renderInput(q), errors[q?.id ?? ""] && (_jsx("div", { className: "tf-hint", role: "alert", children: errors[q?.id ?? ""] })), errors.general && (_jsx("div", { className: "tf-hint", role: "alert", children: errors.general })), _jsxs("div", { className: "tf-actions", children: [_jsx("button", { type: "button", onClick: prev, className: "tf-btn", children: "Back" }), _jsx("button", { type: "button", onClick: next, className: "tf-btn tf-btn-primary", children: "OK" })] })] })) : (_jsxs("div", { children: [_jsx("div", { className: "tf-label", children: "Review" }), errors.general && (_jsx("div", { className: "tf-hint", role: "alert", style: { color: "#dc2626", marginBottom: 16 }, children: errors.general })), _jsxs("div", { className: "tf-actions", children: [_jsx("button", { type: "button", onClick: prev, className: "tf-btn", children: "Back" }), _jsx("button", { type: "button", onClick: handleSubmit, className: "tf-btn tf-btn-primary", disabled: submitting, children: submitting ? "Submitting..." : "Submit" })] }), done && _jsx("div", { className: "tf-success", children: done })] }))] }));
}
